/* eslint-disable no-shadow */
import React, { useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import memoize from 'lodash/memoize';
import isEmpty from 'lodash/isEmpty';

// hooks and contexts
import useAffiliatedRestaurantsState from './useAffiliatedRestaurantsState';
import useEventsState from './useEventsState';
import useResyCoreEffect from './useResyCoreEffect';
import { useRouteParams } from '../../utils/legacyUtils';
import useVenueInventoryState from './useVenueInventoryState';
import useVenueReservationsState from './useVenueReservationsState';
import useVenueState from './useVenueState';
import FocusTrapContext from '../../contexts/focusTrap';
import PathBuilderContext from '../WidgetApp/PathBuilderContext';

// utils and constants
import {
  getLoadReservations, loadVenue, loadVenueInventory,
} from './venueDetailsLoaders';
import { getTranslation } from '../../../../../ui-kitchen/src/providers/TranslationProvider';
import { getEventsUpcoming } from '../../../../../ui-kitchen/src/api';
import { ISO_DATE_FORMAT } from '../../../../../ui-kitchen/src/constants/resyCore';
import { RESY_WEB_ENABLE_2025_CODE_FREEZE_BUG_175, RESY_WEB_ENABLE_2025_CODE_FREEZE_BUG_174 } from '../../../../../ui-kitchen/src/constants/optimizelyExperiments';
import { PENDING } from '../../../../../ui-kitchen/src/constants/asyncLoadingStatuses';
import { getLocationPath } from '../../../../../ui-kitchen/src/utils/locationUtils';
import { getLocationSlug } from '../../../../../ui-kitchen/src/utils';
import venueInventoryUtils from '../../../../../ui-kitchen/src/utils/venueInventoryUtils';
import { getURLSearchParams, setSearchParams } from '../../../../../ui-kitchen/src/utils/searchParamUtils';
import Logger from '../../../../../ui-kitchen/src/utils/logger';

// action, thunks, selectors, reducers
import { selectIsGlobalDiningAccess, setUserLocationLatLong } from '../../../../../ui-kitchen/src/reducers/userSlice';
import { setCountry } from '../../../../../ui-kitchen/src/reducers/countrySlice';
import {
  reset, setCanBook, setDay, setItems, setPartySize, setReservation, setResyToken, setVenueId, setVenueName,
} from '../../../../../ui-kitchen/src/reducers/bookingSlice';
import { getVenueInventoryThunk } from '../../../../../ui-kitchen/src/reducers/venueInventorySlice';
import {
  setSelectedCoreISODate, setSelectedCoreSeatsMinMax, setSelectedCoreSeats, setSelectedCoreTime,
} from '../../../../../ui-kitchen/src/reducers/resyCoreSlice';
import { getVenueReservationsThunk } from '../../../../../ui-kitchen/src/reducers/venueReservationsSlice';
import { selectIsAuthenticated } from '../../../../../ui-kitchen/src/reducers/authTokenSlice';
import { setSignal } from '../../../../../ui-kitchen/src/reducers/signalSlice';

import {
  setMetadataPageTitle, setMobileFirstLogin, setRouteBackPath, setNavVenueId, setNavVenueName,
} from '../../../../../ui-kitchen/src/reducers/resyRouterSlice';

// components
import Loader from '../../../../../ui-kitchen/src/components/Loader';
import AddOnPageController from '../AddOnPage/AddOnPageController';
import VenueDetailsView from './VenueDetailsView';
import { findSlotFromSlots } from '../../../../../ui-kitchen/src/utils/bookingHelpers';

let loginThroughGatedSlotClickTimer = null;

const VenueDetailsController = (props) => {
  // Here are props that are Angular-injected or from Redux mapStateToProps and mapDispatchToProps.
  // Other props needed for render are provided via various custom hooks
  const {
    Analytics,
    backPath,
    bookingState,
    dateLabel,
    getVenueReservationsThunk,
    getVenueInventoryThunk,
    enableFixForRelease174,
    enableFixForRelease175,
    isAddOns,
    isAuthenticated,
    isVenueInventoryLoading,
    isVenueReservationsLoading,
    matchMedia,
    maxQuantity,
    minQuantity,
    platinumNight,
    searchSessionId,
    selectedDate,
    selectedDateFormatted,
    selectedISODate,
    selectedQuantity,
    selectedTime,
    reset,
    setCanBook,
    setCountry,
    setDay,
    setItems,
    setMetadataPageTitle,
    setMobileFirstLogin,
    setNavVenueId,
    setNavVenueName,
    setPartySize,
    setReservation,
    setResyToken,
    setRouteBackPath,
    setSelectedCoreISODate,
    setSelectedCoreSeats,
    setSelectedCoreSeatsMinMax,
    setSelectedCoreTime,
    setSignal,
    setVenueId,
    setVenueName,
    signal,
    templates,
    userIsGda,
    venueInventoryCache,
    venueInventoryData,
    venueInventoryKey,
    venueReservationsData,
    venueReservationsStatus,
    ResyAmexConfig,
  } = props;

  // controller setup
  const focusTrap = useContext(FocusTrapContext);
  const pathBuilder = useContext(PathBuilderContext);
  pathBuilder.setForceSummary(false);
  const searchParams = getURLSearchParams();

  const navigate = useNavigate();
  const loc = useLocation();

  // Venue State setup
  const [venueState, dispatchVenueState] = useVenueState({
    Analytics,
    isAddOns,
    reset,
    setCountry,
    setMetadataPageTitle,
    setMobileFirstLogin,
    setNavVenueId,
    setNavVenueName,
    setSelectedCoreSeatsMinMax,
    setUserLocationLatLong,
    signal,
  });

  // VenueInventory setup
  const { isVenueClosed, inventoryByDay } = useVenueInventoryState({
    venueInventoryData,
    isVenueInventoryLoading,
    venueInventoryKey,
    selectedISODate,
  });

  // Reservations setup
  const [reservationsByServicePeriod, setReservationsByServicePeriod] = useState([]);
  const loadReservations = getLoadReservations({
    getVenueReservationsThunk,
    selectedTime,
    serviceDayCutoff: venueReservationsData?.query?.service_day_cutoff,
    setReservationsByServicePeriod,
  });

  // helper state and util for useVenueReservationsState()
  const [notifyData, setNotifyData] = useState();
  const [serviceTypeId, setServiceTypeId] = useState();
  const openNotify = (serviceId = false) => {
    if (typeof serviceId === 'string') {
      serviceId = parseInt(serviceId, 10); // eslint-disable-line no-param-reassign
    }
    setServiceTypeId(serviceId);
    if (isAuthenticated) {
      setSignal({ name: 'TOGGLE_MODAL', args: serviceId });
    } else {
      setSearchParams('notifyService', serviceId);
      const nextPath = getLocationPath();
      navigate('/login');
      setSearchParams('next', nextPath);
      setSearchParams('authSuccessRedirectUrl', nextPath);
      setSearchParams('serviceId', serviceId);
    }
  };

  const {
    activeNotifications,
    doubleBookingError,
    filterQuery,
    handleReservationSelect,
    displayGatingErrorModal,
    closeModal,
  } = useVenueReservationsState({
    Analytics,
    bookingState,
    dispatchVenueState,
    openNotify,
    platinumNight,
    searchParams,
    searchSessionId,
    selectedDate,
    selectedQuantity,
    setRouteBackPath,
    setCanBook,
    setDay,
    setNotifyData,
    setPartySize,
    setReservation,
    setReservationsByServicePeriod,
    setResyToken,
    setVenueId,
    setVenueName,
    venueReservationsStatus,
    venueReservationsData,
    venueState,
    isAuthenticated,
  });

  // Events setup
  const {
    todayEvents, upcomingEvents, eventsExistOnDay,
  } = useEventsState(venueState.allEvents, selectedISODate);

  // AffiliatedRestaurants setup
  const affiliatedRestaurants = useAffiliatedRestaurantsState({
    venueState,
  });

  // Date / Seats / Time setup
  useResyCoreEffect({
    getVenueInventoryThunk,
    loadReservations,
    selectedDate,
    selectedQuantity,
    selectedTime,
    venueId: venueState.venueId,
  });

  // UI State
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const toggleCalendar = () => setIsCalendarOpen((previousState) => !previousState);

  const [isSafetyMeasuresExpanded, setIsSafetyMeasuresExpanded] = useState(false);
  const expandSafetyMeasures = () => setIsSafetyMeasuresExpanded(true);

  const {
    locationId, venueSlug, venueId: venueIdFromParams, shopsmall,
  } = useRouteParams();

  // On location change useEffect
  useEffect(() => {
    let venueIdRef;
    let quantity;
    let forcedReload;
    loadVenue({ locationId, venueSlug, venueId: venueIdFromParams })
      .then((venue) => {
        venueIdRef = venue.id;
        quantity = (!enableFixForRelease174 || selectedQuantity > venue?.minPartySize) ? selectedQuantity : venue?.minPartySize;
        forcedReload = enableFixForRelease174 ? selectedQuantity < venue?.minPartySize : false;

        dispatchVenueState({ type: 'initializeVenueData', payload: venue });
      })
      .then(() => loadVenueInventory({
        getVenueInventoryThunk, selectedQuantity: quantity, venueId: venueIdRef, forcedReload,
      }))
      .then(() => loadReservations(venueIdRef, selectedDate, quantity))
      .then(() => getEventsUpcoming({ venue_id: venueIdRef }))
      .then((response) => {
        dispatchVenueState({ type: 'setAllEvents', payload: response?.events });
      })
      .then(() => {
        pathBuilder.setSearch(selectedQuantity, selectedDate);
      })
      .catch((e) => Logger.error('** VenueDetailControllerView init error', e));
  }, [loc.pathname, isAuthenticated, enableFixForRelease174]);

  useEffect(() => {
    const clearTokenAndExclusiveId = () => {
      setSearchParams('token', null);
      setSearchParams('exclusiveId', null);
    };
    const loginThroughGatedSlotClick = (reservations, token, exclusiveId) => {
      const slotFound = findSlotFromSlots(reservations, token, exclusiveId);
      if (slotFound) {
        loginThroughGatedSlotClickTimer = setTimeout(() => {
          handleReservationSelect(slotFound, null);
          clearTokenAndExclusiveId();
        }, 1000);
      } else {
        loginThroughGatedSlotClickTimer = setTimeout(() => {
          clearTokenAndExclusiveId();
        }, 1000);
      }
    };

    const token = searchParams.get('token');
    const exclusiveId = searchParams.get('exclusiveId');
    if (reservationsByServicePeriod && venueState && token) {
      loginThroughGatedSlotClick(reservationsByServicePeriod, token, Number(exclusiveId));
    }
    return () => {
      clearTimeout(loginThroughGatedSlotClickTimer);
    };
  }, [reservationsByServicePeriod]);

  // END HOOKS

  // Stateful Venue props
  const {
    aboutVenue,
    centNyServiceTypes,
    centNyTableTypes,
    hasActiveSafetyCategories,
    largePartyMessage,
    needToKnow,
    safetyCategories,
    scrollTop,
    venue,
    venueId,
    venueName,
  } = venueState;
  if (isEmpty(venue)) { return <Loader />; }

  if (enableFixForRelease175) {
    const backToPath = pathBuilder.backPath('Summary');
    if (venueId) {
      pathBuilder.setValues({
        venueId,
      });
    }

    setRouteBackPath(backToPath);
  }

  if (isAddOns) { setRouteBackPath(pathBuilder.backPath('AddOns')); }

  // Translations
  const needToKnowHeader = getTranslation({ translationKey: 'need_to_know_header', fallback: 'Need To Know' });
  const aboutVenueHeader = getTranslation({ translationKey: 'venue_about_header', translationValues: { venueName: venue.name }, fallback: `About X ${venue.name}` });

  // Conditional Render Flags
  const hasAboutVenue = !!aboutVenue;
  const hasNeedToKnow = !!needToKnow?.body;
  const todayEventsCount = todayEvents.length;
  const hasTodayEvents = todayEventsCount > 0;
  const hasAffiliatedRestaurants = affiliatedRestaurants.length > 0;
  const hasPartnershipBranding = !!venue.partnershipBranding;

  // Handlers
  const setSelectedDate = (selectedDateObject) => {
    const selectedISODate = moment(selectedDateObject).format(ISO_DATE_FORMAT);
    setSelectedCoreISODate(selectedISODate);
  };

  // eslint-disable-next-line camelcase
  const onPartySizeChange = ({ party_size }) => {
    setSelectedCoreSeats(party_size);
  };

  const onTimeChange = ({ time }) => {
    setSelectedCoreTime(time);
  };

  const clearTimeFilter = () => {
    setSelectedCoreTime(undefined);
  };

  const handleBookEvent = (event) => {
    const {
      url_slug: urlSlug,
      venue,
    } = event;

    const locationSlug = getLocationSlug(venue.location);
    navigate(`/cities/${locationSlug}/venues/${venue.url_slug}/events/${urlSlug}`);
  };

  const handleTodayEventClick = (event, e) => {
    e.preventDefault();
    const { venue } = venueState;
    const locationSlug = getLocationSlug(venue.location);
    navigate(`/cities/${locationSlug}/venues/${venue.urlSlug}/events/${event.url_slug}`);
  };

  // Render
  return isAddOns
    ? (
      <AddOnPageController
        Analytics={Analytics}
        backPath={backPath}
        bookingState={bookingState}
        selectedDate={selectedDate}
        selectedQuantity={selectedQuantity}
        setItems={setItems}
        templates={templates}
        venue={venue}
      />
    ) : (
      <VenueDetailsView
        Analytics={Analytics}
        aboutVenue={aboutVenue}
        aboutVenueHeader={aboutVenueHeader}
        activeNotifications={activeNotifications}
        affiliatedRestaurants={affiliatedRestaurants}
        isCalendarOpen={isCalendarOpen}
        centNyServiceTypes={centNyServiceTypes}
        centNyTableTypes={centNyTableTypes}
        clearTimeFilter={clearTimeFilter}
        clickableElementIdsToIgnore={['DropdownGroup__selector--date--selection']}
        currentMonth={moment()}
        dateLabel={dateLabel}
        doubleBookingError={doubleBookingError}
        eventsExistOnDay={eventsExistOnDay}
        expandSafetyMeasures={expandSafetyMeasures}
        filterQuery={filterQuery}
        findNextAvailable={venueInventoryUtils.findNextAvailable}
        focusTrap={focusTrap}
        getAriaLabel={venueInventoryUtils.getAriaLabel}
        handleBookEvent={handleBookEvent}
        handleReservationSelect={handleReservationSelect}
        displayGatingErrorModal={displayGatingErrorModal}
        closeModal={closeModal}
        ResyAmexConfig={ResyAmexConfig}
        handleTodayEventClick={handleTodayEventClick}
        hasAboutVenue={hasAboutVenue}
        hasActiveSafetyCategories={hasActiveSafetyCategories}
        hasAffiliatedRestaurants={hasAffiliatedRestaurants}
        hasNeedToKnow={hasNeedToKnow}
        hasPartnershipBranding={hasPartnershipBranding}
        hasTodayEvents={hasTodayEvents}
        inventoryByDay={inventoryByDay}
        isResyCreditEligible={venue.isResyCreditEligible}
        isVenueClosed={isVenueClosed}
        largePartyMessage={largePartyMessage}
        loadReservations={loadReservations}
        isVenueReservationsLoading={isVenueReservationsLoading}
        matchMedia={matchMedia}
        maxQuantity={maxQuantity}
        minQuantity={minQuantity}
        needToKnow={needToKnow}
        needToKnowHeader={needToKnowHeader}
        notifyData={notifyData}
        openNotify={openNotify}
        onPartySizeChange={onPartySizeChange}
        onTimeChange={onTimeChange}
        platinumNight={platinumNight}
        reservations={reservationsByServicePeriod}
        safetyCategories={safetyCategories}
        isSafetyMeasuresExpanded={isSafetyMeasuresExpanded}
        scrollTop={scrollTop}
        selectedDate={selectedDate}
        selectedDateFormatted={selectedDateFormatted}
        selectedISODate={selectedISODate}
        selectedQuantity={selectedQuantity}
        selectedTime={selectedTime}
        serviceTypeId={serviceTypeId}
        setSelectedDate={setSelectedDate}
        shopSmallValue={shopsmall}
        todayEvents={todayEvents}
        todayEventsCount={todayEventsCount}
        toggleCalendar={toggleCalendar}
        upcomingEvents={upcomingEvents}
        userIsGda={userIsGda}
        venue={venue}
        venueId={venueId}
        venueInventoryCache={venueInventoryCache}
        venueName={venueName}
      />
    );
};

const VenueDetailsControllerPropTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  backPath: PropTypes.string.isRequired,
  bookingState: PropTypes.shape({}).isRequired,
  dateLabel: PropTypes.string.isRequired,
  enableFixForRelease174: PropTypes.bool,
  enableFixForRelease175: PropTypes.bool,
  getVenueReservationsThunk: PropTypes.func.isRequired,
  getVenueInventoryThunk: PropTypes.func.isRequired,
  isAddOns: PropTypes.bool,
  isAuthenticated: PropTypes.bool.isRequired,
  isVenueInventoryLoading: PropTypes.bool.isRequired,
  isVenueReservationsLoading: PropTypes.bool.isRequired,
  matchMedia: PropTypes.shape({
    sm: PropTypes.bool,
    md: PropTypes.bool,
  }).isRequired,
  maxQuantity: PropTypes.number.isRequired,
  minQuantity: PropTypes.number.isRequired,
  platinumNight: PropTypes.shape({}),
  reset: PropTypes.func.isRequired,
  searchSessionId: PropTypes.string,
  selectedDate: PropTypes.instanceOf(Date).isRequired,
  selectedDateFormatted: PropTypes.string.isRequired,
  selectedISODate: PropTypes.string.isRequired,
  selectedQuantity: PropTypes.number.isRequired,
  selectedTime: PropTypes.string.isRequired,
  setCanBook: PropTypes.func.isRequired,
  setCountry: PropTypes.func.isRequired,
  setDay: PropTypes.func.isRequired,
  setItems: PropTypes.func.isRequired,
  setMetadataPageTitle: PropTypes.func.isRequired,
  setMobileFirstLogin: PropTypes.func.isRequired,
  setNavVenueId: PropTypes.func.isRequired,
  setNavVenueName: PropTypes.func.isRequired,
  setPartySize: PropTypes.func.isRequired,
  setReservation: PropTypes.func.isRequired,
  setResyToken: PropTypes.func.isRequired,
  setRouteBackPath: PropTypes.func.isRequired,
  setSignal: PropTypes.func.isRequired,
  setSelectedCoreISODate: PropTypes.func.isRequired,
  setSelectedCoreSeats: PropTypes.func.isRequired,
  setSelectedCoreSeatsMinMax: PropTypes.func.isRequired,
  setSelectedCoreTime: PropTypes.func.isRequired,
  setVenueId: PropTypes.func.isRequired,
  setVenueName: PropTypes.func.isRequired,
  signal: PropTypes.shape({}).isRequired,
  templates: PropTypes.shape({}).isRequired,
  userIsGda: PropTypes.bool.isRequired,
  venue: PropTypes.shape({
    announcement: PropTypes.shape({}),
    contact: {
      phone: PropTypes.string,
      url: PropTypes.string,
    },
    customAffiliation: PropTypes.shape({
      id: PropTypes.string,
    }),
    customAffiliations: PropTypes.arrayOf(PropTypes.shape({})),
    fb_pixel: PropTypes.string,
    featureRecaptcha: PropTypes.bool,
    id: PropTypes.number,
    largePartyMessage: PropTypes.string,
    location: PropTypes.shape({
      city: PropTypes.string,
      coords: PropTypes.shape({}),
    }),
    name: PropTypes.string,
    partnershipBranding: PropTypes.shape({
      logo: PropTypes.string,
      name: PropTypes.string,
    }),
    safetyCategories: PropTypes.arrayOf(PropTypes.shape({})),
    safetyInfoUrl: PropTypes.string,
    social: PropTypes.string,
    venueGroup: PropTypes.shape({}),
  }),
  venueInventoryData: PropTypes.shape({
    last_calendar_day: PropTypes.string,
    scheduled: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  venueInventoryCache: PropTypes.shape({}),
  venueInventoryKey: PropTypes.string.isRequired,
  venueReservationsData: PropTypes.shape({
    activeNotifications: PropTypes.shape({}),
    doubleBookingError: PropTypes.shape({}),
    error: PropTypes.shape({}),
    notifyForUnavailable: PropTypes.arrayOf(PropTypes.shape({})),
    partnershipBranding: PropTypes.shape({}),
    query: PropTypes.shape({
      service_day_cutoff: PropTypes.string,
    }),
    reservations: PropTypes.arrayOf(PropTypes.shape({})),
    templates: PropTypes.shape({}),
  }).isRequired,
  venueReservationsStatus: PropTypes.oneOf(['idle', 'pending', 'success', 'failed']).isRequired,
  ResyAmexConfig: PropTypes.shape({ baseRequestUrl: PropTypes.string }),
};

VenueDetailsController.propTypes = VenueDetailsControllerPropTypes;

VenueDetailsController.defaultProps = {
  enableFixForRelease174: false,
  enableFixForRelease175: false,
  isAddOns: false,
  searchSessionId: '',
  venue: null,
  venueInventoryCache: {},
  ResyAmexConfig: undefined,
  platinumNight: null,
};
const getDateInstance = memoize((isoDate) => moment(isoDate, ISO_DATE_FORMAT).toDate());
const getRelativeCalendarDate = (isoDate) => moment(isoDate, ISO_DATE_FORMAT).calendar();
const mapStateToProps = ((state) => {
  const {
    booking, matchMedia, resyCore, resyRouter, signal, venueInventory, venueReservations, experiments,
  } = state;
  const {
    selectedCoreISODate, selectedCoreSeats, selectedCoreSeatsMax, selectedCoreSeatsMin, selectedCoreTime,
  } = resyCore;
  const {
    cache: venueInventoryCache, isLoading: isVenueInventoryLoading, venueInventory: venueInventoryData, venueInventoryKey,
  } = venueInventory;
  const { data: venueReservationsData, status: venueReservationsStatus, templates } = venueReservations;
  const { backPath } = resyRouter;
  return {
    backPath,
    bookingState: booking,
    dateLabel: getRelativeCalendarDate(selectedCoreISODate),
    enableFixForRelease175: experiments?.[RESY_WEB_ENABLE_2025_CODE_FREEZE_BUG_175]?.enabled,
    enableFixForRelease174: experiments?.[RESY_WEB_ENABLE_2025_CODE_FREEZE_BUG_174]?.enabled,
    isAuthenticated: selectIsAuthenticated(state),
    isVenueInventoryLoading,
    isVenueReservationsLoading: venueReservationsStatus === PENDING,
    matchMedia,
    maxQuantity: selectedCoreSeatsMax,
    minQuantity: selectedCoreSeatsMin,
    platinumNight: venueReservationsData?.platinumNight,
    searchSessionId: state.venueSearch?.metadata?.searchSessionId,
    selectedDate: getDateInstance(selectedCoreISODate),
    selectedDateFormatted: getRelativeCalendarDate(selectedCoreISODate),
    selectedISODate: selectedCoreISODate,
    selectedQuantity: selectedCoreSeats,
    selectedTime: selectedCoreTime,
    signal,
    templates,
    userIsGda: selectIsGlobalDiningAccess(state),
    venueInventoryCache,
    venueInventoryData,
    venueInventoryKey,
    venueReservationsData,
    venueReservationsStatus,
  };
});

const mapDispatchToProps = {
  getVenueReservationsThunk,
  getVenueInventoryThunk,
  reset,
  setRouteBackPath,
  setCanBook,
  setCountry,
  setDay,
  setItems,
  setMetadataPageTitle,
  setMobileFirstLogin,
  setNavVenueId,
  setNavVenueName,
  setPartySize,
  setReservation,
  setResyToken,
  setSelectedCoreISODate,
  setSelectedCoreSeats,
  setSelectedCoreSeatsMinMax,
  setSelectedCoreTime,
  setSignal,
  setVenueId,
  setVenueName,
};

export { VenueDetailsController as ComponentUnderTest };
export default connect(mapStateToProps, mapDispatchToProps)(VenueDetailsController);
